import ChainLogo from '@components/ChainLogo'
import TokenAvatar from '@components/TokenAvatar'
import { TransactionOtherParty } from '@modules/UserActivity/Components/TransactionOtherParty'
import { hasOtherParty } from '@plug-core/hooks/useTransactionOtherParty'
import { PlugUserActivity } from '@plug-core/interfaces/transactionActivity'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import { Chip, ListRow, Text, cn } from '@ui'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaArrowRight, FaFire, FaKey, FaLeaf } from 'react-icons/fa6'
import { IoSwapHorizontalOutline } from 'react-icons/io5'

export const ActivityIcon = ({ activity }: { activity: PlugUserActivity }) => {
  switch (activity.kind) {
    case 'Mint':
      return <FaLeaf aria-hidden className="size-3 shrink-0 text-success" />
    case 'Burn':
      return <FaFire aria-hidden className="size-3 shrink-0 text-warning" />
    case 'Approve':
      return <FaKey aria-hidden className="size-3 shrink-0" />
    case 'deposit':
      return <FaArrowRight aria-hidden className="size-3 shrink-0" />
    case 'withdraw':
      return <FaArrowLeft aria-hidden className="size-3 shrink-0" />
    case 'Transfer':
      if (activity.isOutgoing) {
        return <FaArrowRight aria-hidden className="size-3 shrink-0" />
      } else {
        return <FaArrowLeft aria-hidden className="size-3 shrink-0" />
      }
    case 'swap':
    case 'bridge':
      return <IoSwapHorizontalOutline aria-hidden className="size-4 shrink-0" />
  }
}

const TransactionInfo = ({ activity }: { activity: PlugUserActivity }) => {
  const { t } = useTranslation('', { keyPrefix: 'transactionHistory.details' })

  return (
    <>
      {hasOtherParty(activity) && (
        <>
          {activity.isOutgoing ? t('to') : t('from')}{' '}
          <TransactionOtherParty activity={activity} long />
        </>
      )}

      {activity.kind === 'deposit' && `${t('to')} ${activity.provider}`}
      {activity.kind === 'withdraw' && `${t('from')} ${activity.provider}`}
      {activity.isSwap && `${activity.sent.token.symbol} → ${activity.received?.token.symbol}`}
    </>
  )
}

export const ActivityLabel = ({ activity }: { activity: PlugUserActivity }) => {
  const { t } = useTranslation('', { keyPrefix: 'transactionHistory.transactionKinds' })

  let tKey
  if (activity.kind === 'Approve') {
    tKey = 'approved'
  } else if (activity.kind === 'Burn') {
    tKey = 'burned'
  } else if (activity.kind === 'Mint') {
    tKey = 'minted'
  } else if (activity.kind === 'deposit') {
    tKey = 'deposited'
  } else if (activity.kind === 'withdraw') {
    tKey = 'withdrew'
  } else if (activity.kind === 'Transfer') {
    tKey = activity.isOutgoing ? 'transfer.sent' : 'transfer.received'
  } else if (activity.isSwap) {
    tKey = 'swapped'
  }

  return <span className="truncate">{tKey && t(tKey)}</span>
}

/** Small round badge (chain logo, second token) cut out of the row surface. */
const Badge = ({ className, children }: { className?: string; children: ReactNode }) => (
  <span className={cn('absolute flex rounded-full bg-surface p-[3px]', className)}>{children}</span>
)

const ActivityAvatar = ({ activity }: { activity: PlugUserActivity }) =>
  activity.isSwap ? (
    <span className="relative block size-11">
      <TokenAvatar logo={activity.sent.token.logo} size={30} />
      <Badge className="right-0 bottom-0 -mr-1 -mb-1">
        <TokenAvatar logo={activity.received.token.logo} size={28} />
        <Badge className="-right-1 -bottom-1 p-[2px]">
          <ChainLogo chain={activity.received.token.chain} size={12} />
        </Badge>
      </Badge>
    </span>
  ) : (
    <span className="relative block">
      <TokenAvatar logo={activity.sent.token.logo} size={40} />
      <Badge className="-right-1.5 -bottom-1.5">
        <ChainLogo chain={activity.sent.token.chain} size={16} />
      </Badge>
    </span>
  )

export const UserActivityListItem = ({
  activity,
  onClick,
}: {
  activity: PlugUserActivity
  onClick: (activity: PlugUserActivity) => void
}) => {
  const { t } = useTranslation()

  const received = activity.isSwap ? activity.received : activity.sent
  const incoming = activity.isSwap || !activity.isOutgoing

  return (
    <ListRow
      onClick={() => onClick(activity)}
      leading={<ActivityAvatar activity={activity} />}
      primary={
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="grid size-4 shrink-0 place-items-center text-fg-2">
            <ActivityIcon activity={activity} />
          </span>
          <ActivityLabel activity={activity} />

          {activity.state === 'pending' && (
            <Chip tone="pending" className="ml-1 uppercase">
              {t('transactionHistory.pending')}
            </Chip>
          )}
        </span>
      }
      secondary={<TransactionInfo activity={activity} />}
      trailing={
        <span className="flex flex-col items-end">
          <Text
            as="span"
            variant="body-s"
            weight="semibold"
            tabular
            tone={incoming ? 'success' : 'danger'}
          >
            {(activity.isSwap ? '+' : activity.isOutgoing ? '-' : '+') +
              formatNumber(e8sToDecimals(received.amount, received.token.decimals))}
          </Text>
          <Text as="span" variant="caption" tone="fg-2">
            {received.token.symbol}
          </Text>
        </span>
      }
    />
  )
}
